"use client";

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { CheckCircle, GripVertical } from '@/components/icons';
import CloseButton, { CloseIcon } from '@/components/CloseButton';
import { cn } from '@/lib/utils';

interface SortableProductImageItemProps {
  url: string;
  index: number;
  isMainImage: boolean;
  onSetMainImage: (url: string) => void;
  onRemove: (url: string) => void;
  disabled: boolean;
}

const SortableProductImageItemComponent: React.FC<SortableProductImageItemProps> = ({
  url,
  index,
  isMainImage,
  onSetMainImage,
  onRemove,
  disabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: url });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative h-24 w-full min-h-[96px] rounded-md overflow-hidden border group", // Eliminado cursor-grab del contenedor principal
        isDragging && "ring-2 ring-primary ring-offset-2 shadow-lg",
        isMainImage && "ring-2 ring-primary ring-offset-2"
      )}
    >
      <img src={url} alt={`Product image ${index + 1}`} className="w-full h-full object-cover" />
      
      {/* Overlay de acciones */}
      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        
        {/* Botón para arrastrar */}
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/20 mb-1 cursor-grab" // Mantenemos cursor-grab aquí
          {...listeners}
          {...attributes}
          disabled={disabled}
          aria-label="Arrastrar para reordenar"
        >
          <GripVertical className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>

        {/* Botón para eliminar */}
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:bg-destructive/20"
          onClick={() => onRemove(url)}
          disabled={disabled}
          aria-label="Eliminar imagen"
          >
            <CloseIcon className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
      </div>

      {/* Indicador de imagen principal */}
      {isMainImage && (
        <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
          Principal
        </span>
      )}
    </div>
  );
};

// Memoize to avoid unnecessary re-renders when parent re-renders
const SortableProductImageItem = React.memo(SortableProductImageItemComponent);
SortableProductImageItem.displayName = 'SortableProductImageItem';

export default SortableProductImageItem;